import { api } from "config/api";
import { FETCH_STATUS } from "descriptions";
import { makeAutoObservable, runInAction } from "mobx";
import { ILanguage } from "providers/LanguageProvider";
import FetchService, { IFetchDataReturn, IFetchService } from "./FetchService";

export type IEvaluation = "like" | "dislike";

export enum ELocationLabelName {
	Staging = "стейджинг",
	Hotmaps = "Hotmaps",
	Geojet = "GeoJet",
}
export interface IBaseData {
	id: number;
	name: string;
}

export enum EPromoCondition {
	All = "ALL",
	Negative = "NEGATIVE",
	Positive = "POSITIVE",
}

export interface ILocation {
	address: string;
	locale: ILanguage;
	media: Array<{
		category: string;
		category_code?: "LOGO" | "COVER";
		description: string | null;
		id: string | number;
		url: string;
	}>;
	name: string;
	urls: Record<string, string>;
	session_id: string;
	label: { name: ELocationLabelName; url: string };
	promo?: {
		coupons_count: number;
		id: number;
		image?: string;
		name: string;
		hash: string;
		promo_condition: EPromoCondition;
		promo_enable: boolean;
		promo_landing_data: string | null;
	};
}

type FetchLocationDataResponse = Omit<ILocation, "urls" | "locale"> & {
	urls: Record<string, string | null>;
	locale: string;
};

export interface CreateReviewTransition {
	hash: string;
	session_id: string;
	uuid: string;
}

export interface CreateReviewData extends CreateReviewTransition {
	rating_system: ERatingSystem;
	rating_value: number;
}

export interface EditReviewData {
	hash: string;
	session_id: string;
	author: string;
	contacts: string;
	review: string;
	custom_data: string;
}

export interface CreateGeoServiceTransition {
	hash: string;
	session_id: string;
	geo_service_type?: string;
	author?: string;
	contacts?: string;
	promo_id?: number;
	promo_name?: string;
	promo_email?: string;
}

export enum QrSettingsFormFieldType {
	Text = "text",
	Checkbox = "checkbox",
	Contact = "contact",
	Name = "name",
	Description = "description",
	Geoservices = "geoservices",
	SettingsLabelUp = "settingsLabelUp",
}

export interface IAdditionalField {
	id: string;
	type: QrSettingsFormFieldType;
	label: string;
	value: string | string[];
	options?: string[];
	required: boolean;
}

export enum ENegativeStrategy {
	SURVEY = "SURVEY",
	REDIRECT = "REDIRECT",
}

export enum ERatingSystem {
	THUMBS = "THUMBS",
	STARS = "STARS",
	CSAT = "CSAT",
	NPS = "NPS",
}

export interface IMetaLocation {
	geo_services?: null | string[];
	negative_strategy: ENegativeStrategy;
	rating_system: ERatingSystem;
	redirect_link: null | string;
}

export interface IMetaProject extends IBaseData {
	project: IBaseData;
	value: string;
}
export interface IFetchLocationDataMeta {
	setting: { location: IMetaLocation; project: IMetaProject };
}

export interface IReviewState {
	step: number;
	evaluation: IEvaluation | null;
	fetchLocationDataService: IFetchService<FetchLocationDataResponse, IFetchLocationDataMeta>;
	fetchCreateTransitionService: IFetchService;
	fetchCreateReviewService: IFetchService;
	fetchEditReviewService: IFetchService;
	fetchCreateGeoTransitionService: IFetchService;
	location: ILocation;
	additionalFields: IAdditionalField[];
	ratingSystem: ERatingSystem | "";
	ratingValue: number | null;
	negativeStrategy: ENegativeStrategy | "";
	redirectLink: null | string;
	locationGeoservices: string[];
	promoData: { name: string; email: string } | null;
	sessionId: string | null;

	setStep: (step: number) => void;
	setEvaluation: (evaluation: IEvaluation) => void;
	setRatingValue: (value: number) => void;
	fetchLocationData: (
		hash: string,
	) => Promise<IFetchDataReturn<FetchLocationDataResponse, IFetchLocationDataMeta>>;
	fetchCreateTransition: (body: CreateReviewTransition) => Promise<IFetchDataReturn>;
	fetchCreateReview: (body: CreateReviewData) => Promise<IFetchDataReturn>;
	fetchEditReview: (body: EditReviewData) => Promise<IFetchDataReturn>;
	fetchCreateGeoTransition: (body: CreateGeoServiceTransition) => Promise<IFetchDataReturn>;
	resetState: () => void;
	setPromoData: (data: { name: string; email: string } | null) => void;
	setSessionId: (sessionId: string | null) => void;
}

export class ReviewState implements IReviewState {
	step = 0;
	evaluation: IEvaluation | null = null;
	location: ILocation = {
		address: "",
		locale: "en",
		media: [],
		name: "",
		urls: {},
		session_id: "",
		label: { name: ELocationLabelName.Geojet, url: "" },
	};
	ratingSystem: ERatingSystem | "" = "";
	ratingValue: number | null = null;
	negativeStrategy: ENegativeStrategy | "" = "";
	redirectLink: null | string = "";
	fetchLocationDataService: IFetchService<FetchLocationDataResponse, IFetchLocationDataMeta>;
	fetchCreateTransitionService: IFetchService;
	fetchCreateReviewService: IFetchService;
	fetchEditReviewService: IFetchService;
	fetchCreateGeoTransitionService: IFetchService;
	additionalFields: IAdditionalField[] = [];
	locationGeoservices: string[] = [];
	promoData: { name: string; email: string } | null = null;
	sessionId: string | null = null;

	constructor() {
		makeAutoObservable(this);
		this.fetchLocationDataService = new FetchService<FetchLocationDataResponse>();
		this.fetchCreateTransitionService = new FetchService();
		this.fetchCreateReviewService = new FetchService();
		this.fetchEditReviewService = new FetchService();
		this.fetchCreateGeoTransitionService = new FetchService();
	}

	setStep = (step: number) => {
		this.step = step;
	};

	setEvaluation = (evaluation: IEvaluation) => {
		this.evaluation = evaluation;
	};

	setRatingValue = (value: number) => {
		this.ratingValue = value;
	};

	setPromoData = (data: { name: string; email: string } | null) => {
		this.promoData = data;
	};

	fetchLocationData = (hash: string) =>
		this.fetchLocationDataService.fetchData({ url: api.getLocationData(hash) }).then(response => {
			const { data, status, meta } = response;
			if (status === FETCH_STATUS.DONE && data) {
				runInAction(() => {
					this.sessionId = data.session_id;

					const urls = Object.keys(data.urls).reduce((acc, item) => {
						if (data.urls[item] === null) return acc;
						return { ...acc, [item]: data.urls[item] as string };
					}, {});
					const locale = data.locale.substring(0, 2) as ILanguage;

					this.location = { ...data, urls, locale };

					if (meta && meta?.setting?.location?.geo_services) {
						this.locationGeoservices = meta.setting.location.geo_services;
					}

					if (
						meta &&
						meta.setting?.project?.value?.length &&
						Array.isArray(JSON.parse(meta.setting.project.value))
					) {
						this.additionalFields = JSON.parse(meta.setting.project.value);
						this.ratingSystem = meta.setting?.location?.rating_system;
						this.negativeStrategy = meta.setting?.location?.negative_strategy;
						this.redirectLink = meta.setting?.location?.redirect_link;
					}
				});
			}
			return response;
		});

	fetchCreateTransition = (body: CreateReviewTransition) =>
		this.fetchCreateTransitionService.fetchData({
			url: api.createTransition,
			method: "POST",
			body,
		});

	fetchCreateReview = (body: CreateReviewData) =>
		this.fetchCreateReviewService.fetchData({ url: api.createReview, method: "PATCH", body });

	fetchEditReview = (body: EditReviewData) =>
		this.fetchEditReviewService.fetchData({ url: api.createReview, method: "PATCH", body });

	fetchCreateGeoTransition = (body: CreateGeoServiceTransition) =>
		this.fetchCreateGeoTransitionService.fetchData({
			url: api.createGeoServiceTransition,
			method: "PATCH",
			body,
		});

	setSessionId = (sessionId: string | null) => {
		this.sessionId = sessionId;
	};

	resetState = () => {
		this.step = 0;
		this.evaluation = null;
		this.location = {
			address: "",
			locale: "en",
			media: [],
			name: "",
			urls: {},
			session_id: "",
			label: { name: ELocationLabelName.Geojet, url: "" },
		};
		this.ratingSystem = "";
		this.ratingValue = null;
		this.negativeStrategy = "";
		this.redirectLink = null;
		this.additionalFields = [];
		this.locationGeoservices = [];
		this.promoData = null;
		this.sessionId = null;
	};
}
